import { useQuery } from "@apollo/client";
import type { Query } from "@models/ISchema";
import curriculumTeachingDatesQuery from "../../graphql/curriculumTeachingDates.query.graphql";

interface UseCurriculumTeachingDatesParams {
  scenarioId: string;
  programId?: string;
  curriculumCode: string;
}

type CurriculumTeachingDatesQueryData = Pick<Query, "data">;

/**
 * Reads the selected curriculum's tags. Two returned rows reveal an ambiguous
 * program/code match, so neither row is used for the summary.
 */
export const useCurriculumTeachingDates = ({
  scenarioId,
  programId,
  curriculumCode,
}: UseCurriculumTeachingDatesParams) => {
  const { data, loading, error, refetch } = useQuery<CurriculumTeachingDatesQueryData>(
    curriculumTeachingDatesQuery,
    {
      variables: {
        scenarioId,
        filter: {
          fields: {
            program_id: { eq: Number(programId) },
            code: { eq: curriculumCode },
          },
          groupByCode: false,
          pagination: { page: 1, size: 2 },
        },
      },
      skip: !scenarioId || !programId || !curriculumCode,
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    },
  );

  const curriculums = data?.data?.curriculums?.items ?? [];

  return {
    tags: curriculums.length === 1 ? (curriculums[0].tags ?? null) : null,
    isLoading: loading,
    error,
    retry: refetch,
  };
};

export default useCurriculumTeachingDates;
